import { Company } from 'module/api/company/types';
import { logger } from 'module/logger';
import { Loading, PageLoading } from 'module/ui/components/atoms/loading';
import { Body01 } from 'module/ui/ozone/atoms/typography';
import { GroupedTimelines } from 'api/timeline/types';
import moment from 'moment';
import { useGroupByMonthQuery, useTimelineListQuery } from 'queries/timeline';
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import styled from 'styled-components';
import { CalendarList } from 'components/calendar-list';
import {
  CenterAlign,
  FlexWrapperJustifyBetween,
} from 'components/common/styles';
import { TimelineDetails } from 'components/organisms/timeline-details';
// import { AccountHeader } from './common/components';
import { AccountLayout } from './common/layout';
import { AccountsTabs, AccountsTabsType } from './common/tab';
import { useCompanyFromUrl } from './use-current-company';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: auto;
  background-color: var(--scarlet-gray-10);
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: auto;
  background-color: var(--scarlet-gray-10);
`;

const StyledFlexWrapper = styled(FlexWrapperJustifyBetween)`
  display: flex;
  width: 100%;
  min-height: calc(100vh - 64px);
  height: 100vh;
  overflow: auto;
  margin-bottom: 10rem;
`;

const LeftColumn = styled.div`
  height: calc(100vh - 150px);
  overflow: auto;
  width: 320px;
  position: fixed;
`;

const MainContent = styled.div.attrs({ className: 'main-content' })`
  flex: 1;
  display: flex;
  height: 100vh;
  overflow: auto;
  width: 100%;
`;

const MiddleColumn = styled.div`
  /* 560 + 120 + 120 */
  /* width: 800px; */
  flex-shrink: 0;
  max-width: 100%;
  display: flex;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const CalendarListWrapper = styled.div`
  padding: var(--spacing-06) var(--spacing-06) var(--spacing-06) 0;
  margin-bottom: var(--spacing-10);
`;

const Wrapper = styled.div`
  padding: 0 0 var(--spacing-06) var(--spacing-06);
  background-color: var(--scarlet-gray-10);
  height: calc(100vh - 200px);
`;

const formatTimelinesForCalendar = (
  data: GroupedTimelines,
): Record<
  string,
  {
    timelineId: number;
    title: string;
    summaryAvailable: boolean;
    signalAwareRecap: string;
  }[]
> => {
  if (!data || Object.keys(data).length === 0) {
    return {};
  }

  const groupedTimelines = Object.entries(data)?.reduce(
    (acc: any, [date, timelines]) => {
      if (!acc[date]) {
        acc[moment(date).format('MMM YYYY')] = [];
      }
      acc[moment(date).format('MMM YYYY')].push(
        ...timelines.map((m) => ({
          timelineId: m?.source?.id,
          title: m?.source?.title ?? '',
          summaryAvailable: m?.summaryAvailable ?? false,
          signalAwareRecap: m?.signalAwareRecap ?? '',
        })),
      );

      return acc;
    },
    {},
  );

  return groupedTimelines;
};

interface TimelineListProps {
  company: Company;
}
export const TimelineList: React.FC<TimelineListProps> = ({ company }) => {
  const {
    data: timelines,
    isLoading: isTimelineLoading,
    isError,
  } = useTimelineListQuery(company.companyId);

  const {
    data: groupByMonth,
    isLoading: isGroupByMonthLoading,
    isError: isGroupByMonthError,
  } = useGroupByMonthQuery(company.companyId);

  const formattedTimelines = useMemo(
    () => formatTimelinesForCalendar(groupByMonth ?? {}),
    [groupByMonth],
  );

  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedTimeline, setSelectedTimeline] = useState<number | null>(null);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(
    Object.keys(formattedTimelines)[0] ?? null,
  );

  const updateSelectedTimeline = useCallback(
    (timelineId: number) => {
      if (!isNaN(timelineId) && timelineId !== selectedTimeline) {
        lastUpdateRef.current = Date.now();
        setSelectedTimeline(timelineId);

        // Find the month for this timeline
        const monthForTimeline = Object.entries(formattedTimelines).find(
          ([_, timelines]) =>
            timelines.some((m) => m.timelineId === timelineId),
        )?.[0];

        if (monthForTimeline) {
          setExpandedMonth(monthForTimeline);
        }
      }
    },
    [selectedTimeline, formattedTimelines],
  );

  const handleScroll = useCallback(
    (scrollContainer: Element) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const elements = document.querySelectorAll('[data-timeline-id]');
        const containerRect = scrollContainer.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;

        let closestElement: HTMLElement | null = null;
        let minDistance = Infinity;

        elements.forEach((element) => {
          const rect = element.getBoundingClientRect();
          const elementCenter = rect.top + rect.height / 2;
          const distance = Math.abs(elementCenter - containerCenter);

          if (distance < minDistance) {
            minDistance = distance;
            closestElement = element as HTMLElement;
          }
        });

        if (closestElement) {
          const timelineId = Number(
            (closestElement as HTMLElement)?.getAttribute('data-timeline-id'),
          );

          updateSelectedTimeline(timelineId);
        }
      }, 50);
    },
    [updateSelectedTimeline],
  );

  useEffect(() => {
    const scrollContainer = document.querySelector('.main-content');

    if (scrollContainer) {
      // Setup scroll handler
      const scrollHandler = () => handleScroll(scrollContainer);

      scrollContainer.addEventListener('scroll', scrollHandler);

      // Setup Intersection Observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          // Only process if we haven't updated recently
          const now = Date.now();

          if (now - lastUpdateRef.current < 100) {
            return;
          }

          const visibleEntries = entries.filter(
            (entry) => entry.isIntersecting && entry.intersectionRatio > 0.3,
          );

          if (visibleEntries.length > 0) {
            const sortedByPosition = visibleEntries.sort((a, b) => {
              const rectA = a.boundingClientRect;
              const rectB = b.boundingClientRect;

              return Math.abs(rectA.top) - Math.abs(rectB.top);
            });

            const topMostTimeline = sortedByPosition[0];
            const timelineId = Number(
              topMostTimeline.target.getAttribute('data-timeline-id'),
            );

            updateSelectedTimeline(timelineId);
          }
        },
        {
          root: scrollContainer,
          rootMargin: '-10% 0px -10% 0px',
          threshold: [0.5],
        },
      );

      // Observe all timeline elements
      const elements = document.querySelectorAll('[data-timeline-id]');

      elements.forEach((element) => {
        observerRef.current?.observe(element);
      });

      // Cleanup
      return () => {
        scrollContainer.removeEventListener('scroll', scrollHandler);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }
  }, [timelines, selectedTimeline, handleScroll, updateSelectedTimeline]);

  // Get current month and year

  const handleTimelineClick = async (
    timeline: { timelineId: number; title: string },
    month: string,
  ) => {
    try {
      setSelectedTimeline(timeline.timelineId);
      setExpandedMonth(month);

      // Get the element to scroll to
      const element = document.querySelector(
        `[data-timeline-id="${timeline.timelineId}"]`,
      );

      if (element) {
        // Get the scroll container (MainContent)
        const scrollContainer = document.querySelector('.main-content');

        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          // Calculate relative position within the scroll container
          const scrollTop =
            elementRect.top -
            containerRect.top +
            scrollContainer.scrollTop -
            20;

          // Scroll the container
          scrollContainer.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          });
        }
      }
    } catch (error) {
      logger.error('Failed to fetch timeline details:', { error });
    }
  };

  const handleMonthClick = (month: string) => {
    // Simply toggle the month expansion
    setExpandedMonth(expandedMonth === month ? null : month);

    // Only clear selected timeline if we're collapsing the month that contains it
    if (
      expandedMonth === month &&
      formattedTimelines[month]?.some((m) => m.timelineId === selectedTimeline)
    ) {
      setSelectedTimeline(null);
    }
  };

  if (isTimelineLoading || isGroupByMonthLoading) {
    return (
      <CenterAlign>
        <Loading size="large" />
      </CenterAlign>
    );
  }

  if (isError || isGroupByMonthError) {
    return (
      <CenterAlign>
        <Body01>Error loading timelines timeline</Body01>
      </CenterAlign>
    );
  }

  if (timelines?.length === 0 || !groupByMonth) {
    return (
      <CenterAlign>
        <Body01>No timelines found</Body01>
      </CenterAlign>
    );
  }

  return (
    <PageWrapper>
      {/* <AccountHeader /> */}
      <ContentWrapper>
        <StyledFlexWrapper>
          <LeftColumn>
            <CalendarListWrapper>
              <CalendarList
                timelines={formattedTimelines}
                selectedTimeline={selectedTimeline}
                expandedMonth={
                  expandedMonth ?? Object.keys(formattedTimelines)[0] ?? ''
                }
                onTimelineClick={handleTimelineClick}
                onMonthClick={handleMonthClick}
              />
            </CalendarListWrapper>
          </LeftColumn>
          <MainContent>
            <MiddleColumn>
              <TimelineDetails timelines={timelines ?? []} />
            </MiddleColumn>
          </MainContent>
        </StyledFlexWrapper>
      </ContentWrapper>
    </PageWrapper>
  );
};

export const AccountTimelinePage = () => {
  const { data: company, isLoading: isCompanyLoading } = useCompanyFromUrl();

  return isCompanyLoading || company == null ? (
    <PageLoading />
  ) : (
    <AccountLayout
      company={company}
      tabs={<AccountsTabs activeKey={AccountsTabsType.Timeline} />}
      gray={true}
      content={
        <Wrapper>
          <TimelineList company={company} />
        </Wrapper>
      }
      actions={{}}
    />
  );
};
